/**
 * TrusteesReport — a quarter of confidential financial access, on one page.
 *
 * Written to be printed and handed over: who was allowed to see which figures,
 * on whose word, for how long, how often they actually looked, and whether the
 * security trail still verifies.
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, Loader2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = body.detail;
    throw new Error(typeof d === 'string' ? d : (d?.reason || `Request failed (${res.status})`));
  }
  return body;
};

const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const day = (v) => (v ? new Date(v).toLocaleDateString() : '—');

/** Who this page is sent to, and the board dates it is sent a week before. */
const ReportSchedule = () => {
  const qc = useQueryClient();
  const schedule = useQuery({
    queryKey: ['report-schedule'],
    queryFn: () => api('/finance/sensitive/report/schedule'),
    retry: false,
  });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [dates, setDates] = useState('');

  useEffect(() => {
    if (!schedule.data) return;
    setEmail(schedule.data.recipient_email || '');
    setName(schedule.data.recipient_name || '');
    setDates((schedule.data.meeting_dates || []).join(', '));
  }, [schedule.data]);

  const save = async () => {
    try {
      const body = {
        recipient_email: email.trim(),
        recipient_name: name.trim(),
        meeting_dates: dates.split(',').map((d) => d.trim()).filter(Boolean),
      };
      const res = await fetch(`${API_URL}/api/finance/sensitive/report/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof out.detail === 'string' ? out.detail : 'Could not save');
      toast.success(email.trim()
        ? 'Saved — the page goes out a week before each meeting'
        : 'Saved — nothing will be sent until a recipient is named');
      qc.invalidateQueries({ queryKey: ['report-schedule'] });
    } catch (e) { toast.error(e.message); }
  };

  if (schedule.isError) return null;

  return (
    <div className="rounded-lg border px-4 py-3 print:hidden space-y-2"
         data-testid="report-schedule-form">
      <div>
        <p className="text-sm font-medium">Send this to the trustees</p>
        <p className="text-xs text-slate-600 dark:text-slate-400">
          A week before each board meeting, so it is read beforehand. Leave the
          address empty and nothing is sent.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-400">Secretary's email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-1 h-9 w-60 rounded-md border px-2 text-sm bg-transparent"
                 placeholder="secretary@trustees.org"
                 data-testid="report-email-input" />
        </div>
        <div>
          <label className="block text-xs text-slate-600 dark:text-slate-400">Name (optional)</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-1 h-9 w-44 rounded-md border px-2 text-sm bg-transparent"
                 data-testid="report-name-input" />
        </div>
        <div className="flex-1 min-w-[16rem]">
          <label className="block text-xs text-slate-600 dark:text-slate-400">
            Board meeting dates (2026-09-14, 2026-12-07)
          </label>
          <input value={dates} onChange={(e) => setDates(e.target.value)}
                 className="mt-1 h-9 w-full rounded-md border px-2 text-sm bg-transparent"
                 placeholder="2026-09-14, 2026-12-07"
                 data-testid="report-dates-input" />
        </div>
        <Button size="sm" onClick={save} data-testid="report-schedule-save-btn">Save</Button>
      </div>
      {schedule.data?.last_sent && Object.keys(schedule.data.last_sent).length > 0 && (
        <p className="text-xs text-slate-600 dark:text-slate-400" data-testid="report-last-sent">
          Last sent: {Object.entries(schedule.data.last_sent)
            .map(([date, info]) => `${date} ${info.ok ? '✓' : '✗'}`).join(' · ')}
        </p>
      )}
    </div>
  );
};

export const TrusteesReport = () => {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [quarter, setQuarter] = useState(String(Math.floor(now.getMonth() / 3) + 1));

  const report = useQuery({
    queryKey: ['sensitive-report', year, quarter],
    queryFn: () => api(`/finance/sensitive/report?year=${year}&quarter=${quarter}`),
    retry: false,
  });

  const d = report.data;

  return (
    <div className="space-y-4" data-testid="trustees-report">
      <div className="flex flex-wrap items-end gap-2 print:hidden">
        <Select value={quarter} onValueChange={setQuarter}>
          <SelectTrigger className="w-32" data-testid="report-quarter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {['1', '2', '3', '4'].map((q) => <SelectItem key={q} value={q}>Quarter {q}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28" data-testid="report-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[0, 1, 2].map((back) => {
              const y = String(now.getFullYear() - back);
              return <SelectItem key={y} value={y}>{y}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => window.print()}
                data-testid="report-print-btn">
          <Printer className="h-4 w-4 mr-1" /> Print
        </Button>
      </div>

      <ReportSchedule />

      {report.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
      {report.isError && <p className="text-sm text-rose-600">{report.error.message}</p>}

      {d && (
        <article className="rounded-lg border p-6 bg-white dark:bg-slate-900 space-y-5">
          <header>
            <h2 className="text-xl font-semibold">
              Confidential financial access · Q{d.period.quarter} {d.period.year}
            </h2>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
              {day(d.period.from)} to {day(d.period.to)} · working day counted as {d.period.working_day} ·
              produced {when(d.generated_at)}
            </p>
          </header>

          <p className="text-sm leading-relaxed">{d.statement}</p>

          {d.summary.times_opened > 0 && d.summary.grants === 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="report-earlier-grants">
              The figures were opened in this quarter under authorisations granted
              before it. The grants themselves are listed in the quarter they were
              approved in; every opening is in the security trail referenced below.
            </p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              ['People granted', d.summary.people_granted],
              ['Grants', d.summary.grants],
              ['Of those, emergency', d.summary.emergency_grants],
              ['Times opened', d.summary.times_opened],
              ['Outside working hours', d.summary.out_of_hours_viewings],
              ['Requests refused', d.summary.requests_refused],
              ['Emergencies that lapsed', d.summary.emergency_requests_lapsed],
              ['Attempts denied', d.summary.attempts_denied],
            ].map(([label, value]) => (
              <div key={label} className="rounded border px-3 py-2">
                <p className="text-[11px] text-slate-600 dark:text-slate-400">{label}</p>
                <p className="text-lg font-semibold">{value}</p>
              </div>
            ))}
          </div>

          <section>
            <h3 className="text-sm font-semibold mb-2">Every grant in the quarter</h3>
            {d.grants.length === 0 ? (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Nobody was granted access to confidential financial information in this quarter.
              </p>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left text-slate-600 dark:text-slate-400">
                    <th className="py-1.5 pr-2 font-medium">Who</th>
                    <th className="py-1.5 pr-2 font-medium">What</th>
                    <th className="py-1.5 pr-2 font-medium">From</th>
                    <th className="py-1.5 pr-2 font-medium">For</th>
                    <th className="py-1.5 pr-2 font-medium">Opened</th>
                    <th className="py-1.5 font-medium">Approved by</th>
                  </tr>
                </thead>
                <tbody>
                  {d.grants.map((g, i) => (
                    <tr key={i} className="border-t align-top" data-testid={`report-grant-${i}`}>
                      <td className="py-1.5 pr-2">{g.who}</td>
                      <td className="py-1.5 pr-2">
                        {g.what}{g.emergency && <span className="text-rose-600"> · emergency</span>}
                      </td>
                      <td className="py-1.5 pr-2">{when(g.granted_at)}</td>
                      <td className="py-1.5 pr-2">
                        {g.duration}{g.revoked_at ? ' · revoked' : ''}
                      </td>
                      <td className="py-1.5 pr-2">{g.times_viewed}×</td>
                      <td className="py-1.5">{(g.approved_by || []).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-2">Financial Information Custodians</h3>
            <ul className="text-xs space-y-1">
              {d.custodians.map((c, i) => (
                <li key={i}>
                  {c.who}{c.ecosystem_wide ? ' · may decide ecosystem-wide requests' : ''}
                  {c.appointed_at ? ` · appointed ${day(c.appointed_at)}` : ''}
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded border px-3 py-2 text-xs" data-testid="report-integrity">
            <p className={d.integrity.verified ? 'text-emerald-700' : 'text-rose-700'}>
              {d.integrity.verified
                ? `The security trail verifies: ${d.integrity.records} records, unaltered.`
                : `The security trail does not verify. ${d.integrity.note}`}
            </p>
            <p className="text-slate-600 dark:text-slate-400 mt-1 break-all">
              Head digest {d.integrity.head_hash}
              {d.integrity.sealed_with_key ? ' (sealed with the application key)' : ' (not keyed)'}
            </p>
          </section>
        </article>
      )}
    </div>
  );
};

export default TrusteesReport;
